import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Card } from "@/components/Shell";
import { Download, Upload, FileSpreadsheet, CheckCircle2 } from "lucide-react";

const KINDS = [
  { key: "products", label: "Products", desc: "Category, Brand, Model auto-create." },
  { key: "warehouses", label: "Warehouses", desc: "Bulk warehouse creation." },
  { key: "engineers", label: "Engineers", desc: "Create engineers with cluster head mapping." },
  { key: "opening_stock", label: "Opening Stock", desc: "Initial stock per warehouse + serials." },
];

export default function ExcelImport() {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const sample = async (kind) => {
    const res = await api.get(`/excel/sample/${kind}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `sample_${kind}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(()=>URL.revokeObjectURL(url), 500);
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setErr(""); setResult(null); setBusy(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/excel/import/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult({ kind, ...data });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="p-4 md:p-8" data-testid="excel-page">
      <PageHeader title="Excel Bulk Import" subtitle="Download sample templates and upload completed files." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {KINDS.map(k => (
          <Card key={k.key} className="p-5">
            <div className="flex items-start justify-between mb-2">
              <FileSpreadsheet className="text-[#F97316]" size={22} />
              <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{k.key}</span>
            </div>
            <div className="font-display font-semibold text-base">{k.label}</div>
            <div className="text-xs text-zinc-500 mt-1 mb-4">{k.desc}</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={()=>sample(k.key)} data-testid={`sample-${k.key}`} className="inline-flex items-center gap-2 text-xs font-semibold border border-zinc-200 hover:border-[#F97316] hover:text-[#F97316] py-2 px-3 rounded-md">
                <Download size={13} /> Sample
              </button>
              <label className="inline-flex items-center gap-2 text-xs font-semibold bg-[#F97316] hover:bg-[#EA580C] text-white py-2 px-3 rounded-md cursor-pointer" data-testid={`upload-${k.key}-label`}>
                <Upload size={13} /> {busy === k.key ? "Uploading…" : "Upload"}
                <input type="file" accept=".xlsx,.xls" className="hidden" data-testid={`upload-${k.key}`} onChange={e=>upload(k.key, e.target.files?.[0])} />
              </label>
            </div>
          </Card>
        ))}
      </div>

      {err && <div className="mt-4 text-rose-600 text-sm">{err}</div>}
      {result && (
        <Card className="p-5 mt-6" data-testid="import-result">
          <div className="flex items-center gap-2 mb-3"><CheckCircle2 className="text-emerald-600" size={18} /><div className="font-display font-semibold">Import result — {result.kind}</div></div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div><div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Inserted</div><div className="num font-bold text-2xl text-emerald-600">{result.inserted}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Duplicates</div><div className="num font-bold text-2xl text-amber-600">{result.duplicates}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Errors</div><div className="num font-bold text-2xl text-rose-600">{result.errors?.length || 0}</div></div>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-3 max-h-60 overflow-auto text-xs border border-zinc-100 rounded-md">
              {result.errors.map((e, i) => (
                <div key={i} className="px-3 py-2 border-b border-zinc-100">Row {e.row}: <span className="text-rose-600">{e.error}</span></div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
